import { Box, Flex, Text, Button, useDisclosure } from '@chakra-ui/react';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { MeContext } from '../../../context/MeContext';
import { useBilling } from '../../../store/client/BillingContext';
import GoLiveModal from '../subscription/GoLiveModal';
import ResubscribeModal from '../subscription/ResubscribeModal';
import CardlessTrialConversionModal from '../subscription/CardlessTrialConversionModal';
import SubscriptionComparisonModal from '../subscription/SubscriptionComparisonModal';
import RequestCustomPlanModal from '../subscription/RequestCustomPlanModal';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useIsReadOnly } from '../../../hooks/useIsReadOnly';

function TrialProgressBox({ buttonVariant = 'gradient' }) {
  const navigate = useNavigate();
  const { subscription, refresh, customPlanRequest } = useBilling();
  const meCtx = useContext(MeContext);
  const [isGoLiveModalOpen, setIsGoLiveModalOpen] = useState(false);
  const [isResubscribeModalOpen, setIsResubscribeModalOpen] = useState(false);
  const [isCardlessModalOpen, setIsCardlessModalOpen] = useState(false);
  const subscriptionComparisonModal = useDisclosure();
  const requestCustomPlanModal = useDisclosure();
  const [customPlanBillingInterval, setCustomPlanBillingInterval] =
    useState(null);
  const [stripePromise, setStripePromise] = useState(null);

  const {
    onTrial: subscriptionOnTrial,
    daysRemainingInTrial: subscriptionDaysRemainingInTrial,
    status: subscriptionStatus,
  } = subscription || {
    onTrial: false,
    daysRemainingInTrial: 0,
    status: '',
  };

  const {
    organisationStatus,
    cardlessTrialStartedAt,
    trialExpiry,
    billingEnabled,
  } = meCtx.state;

  const isReadOnly = useIsReadOnly();

  const onCardlessTrial =
    organisationStatus === 'trial' && cardlessTrialStartedAt;

  const isCardlessTrialExpired =
    organisationStatus === 'trial_expired' && cardlessTrialStartedAt;

  const isStripeTrialExpired =
    organisationStatus === 'locked' &&
    billingEnabled &&
    subscriptionStatus === 'canceled';

  useEffect(() => {
    if (
      subscriptionOnTrial ||
      onCardlessTrial ||
      isCardlessTrialExpired ||
      isStripeTrialExpired
    ) {
      const stripeKey =
        window.env?.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
        process.env?.REACT_APP_STRIPE_PUBLISHABLE_KEY;

      if (stripeKey) {
        const promise = loadStripe(stripeKey);
        setStripePromise(promise);
      }
    }
  }, [
    subscriptionOnTrial,
    onCardlessTrial,
    isCardlessTrialExpired,
    isStripeTrialExpired,
  ]);

  if (isReadOnly) {
    return null;
  }

  if (
    !subscriptionOnTrial &&
    !onCardlessTrial &&
    !isCardlessTrialExpired &&
    !isStripeTrialExpired
  ) {
    return null;
  }

  const cardlessTrialDaysRemaining = trialExpiry
    ? Math.ceil((new Date(trialExpiry) - new Date()) / (1000 * 60 * 60 * 24))
    : 0;

  const daysRemainingInTrial = subscriptionOnTrial
    ? subscriptionDaysRemainingInTrial
    : cardlessTrialDaysRemaining;

  const handleGoLiveClick = () => {
    if (isStripeTrialExpired) {
      setIsResubscribeModalOpen(true);
    } else if (subscriptionOnTrial) {
      setIsGoLiveModalOpen(true);
    } else if (onCardlessTrial || isCardlessTrialExpired) {
      setIsCardlessModalOpen(true);
    }
  };

  return (
    <Box
      p={isCardlessTrialExpired ? 6 : 4}
      bg={isCardlessTrialExpired ? '#FEF3F2' : 'white'}
      mt={{ base: '10px', md: '0' }}
      mb={6}
      borderRadius="15px"
      position="relative"
      _before={{
        content: '""',
        position: 'absolute',
        inset: 0,
        padding: '1.5px',
        borderRadius: 'inherit',
        background: 'linear-gradient(180deg, #FF9554 16%, #FF4935 100%)',
        WebkitMask:
          'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        WebkitMaskComposite: 'xor',
        maskComposite: 'exclude',
        pointerEvents: 'none',
      }}
    >
      <Flex direction="column" alignItems="center" gap={3}>
        {isCardlessTrialExpired || isStripeTrialExpired ? (
          <>
            <Text
              fontSize="22px"
              color="#000000"
              textStyle="headingFamilyBold"
              textAlign="center"
            >
              Your free trial has ended.
            </Text>
            <Text
              fontSize="16px"
              color="#475467"
              textStyle="bodyFamilyRegular"
              textAlign="center"
              mb={1}
            >
              Your account is now locked. Go live to restore full access
              instantly
            </Text>
            <Button
              variant={
                isStripeTrialExpired || isCardlessTrialExpired
                  ? 'gradient'
                  : buttonVariant
              }
              px={12}
              onClick={handleGoLiveClick}
            >
              {isStripeTrialExpired ? 'Resubscribe' : 'Select a plan'}
            </Button>
          </>
        ) : (
          <>
            {daysRemainingInTrial > 1 ? (
              <Text
                fontSize={{ base: '16px', md: '18px' }}
                color="#000000"
                textStyle="bodyFamilyRegular"
                textAlign="center"
                whiteSpace={{ base: 'normal', md: 'nowrap' }}
              >
                You have{' '}
                <Text
                  as="span"
                  fontSize={{ base: '16px', md: '18px' }}
                  fontWeight="bold"
                >
                  {daysRemainingInTrial} days left of your free trial
                </Text>
              </Text>
            ) : (
              <Text
                fontSize={{ base: '16px', md: '18px' }}
                color="#000000"
                textStyle="bodyFamilyRegular"
              >
                Your trial ends today
              </Text>
            )}
            <Flex
              direction={{ base: 'column', md: 'row' }}
              gap="10px"
              width="100%"
            >
              {subscriptionOnTrial && (
                <Button
                  variant="secondary"
                  width="100%"
                  onClick={() => {
                    subscriptionComparisonModal.onOpen();
                  }}
                >
                  <Text>Upgrade Plan</Text>
                </Button>
              )}
              <Button
                variant={
                  isStripeTrialExpired || isCardlessTrialExpired
                    ? 'gradient'
                    : buttonVariant
                }
                width="100%"
                onClick={handleGoLiveClick}
              >
                {subscriptionOnTrial ? 'Go Live' : 'Select a plan'}
              </Button>
            </Flex>
          </>
        )}
      </Flex>
      {stripePromise && (
        <Elements stripe={stripePromise}>
          <GoLiveModal
            isOpen={isGoLiveModalOpen}
            onClose={() => setIsGoLiveModalOpen(false)}
          />
          <ResubscribeModal
            isOpen={isResubscribeModalOpen}
            handleClose={() => setIsResubscribeModalOpen(false)}
            onSuccess={async () => {
              setIsResubscribeModalOpen(false);
              await refresh();
              await meCtx.fetchData();
            }}
            subscription={subscription}
          />
          <CardlessTrialConversionModal
            isOpen={isCardlessModalOpen}
            onClose={() => setIsCardlessModalOpen(false)}
          />
          <SubscriptionComparisonModal
            isOpen={subscriptionComparisonModal.isOpen}
            customPlanRequest={customPlanRequest}
            onOpenRequestCustomPlan={(interval) => {
              setCustomPlanBillingInterval(interval);
              requestCustomPlanModal.onOpen();
            }}
            onClose={() => {
              subscriptionComparisonModal.onClose();
            }}
          />
          <RequestCustomPlanModal
            isOpen={requestCustomPlanModal.isOpen}
            onClose={() => {
              setCustomPlanBillingInterval(null);
              requestCustomPlanModal.onClose();
            }}
            onSuccess={async () => {
              subscriptionComparisonModal.onClose();
              if (refresh) {
                await refresh();
              }
              navigate('/my-account');
            }}
            subscription={subscription}
            selectedInterval={
              customPlanBillingInterval ??
              subscription?.plan?.interval ??
              'year'
            }
          />
        </Elements>
      )}
    </Box>
  );
}

export default TrialProgressBox;
